/**
 * Dynamic Flow Button that supports server styles.
 */
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import { colorCss, fontWeightCss, sizeCss, StyleValueTextTransform } from 'lib/inquiryStyles';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
}

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  // This is now solely used for data-test and not for appearance.
  buttonType?: ButtonType;
  disabled?: boolean;
  href?: string;
  type?: 'button' | 'reset' | 'submit';
  styles?: ButtonComponentStyles;
  isPending?: boolean;
  uploadProgress?: number;
  pendingContent?: ReactNode;
}

interface WithVisibleProps {
  isVisible: boolean;
}

const StyledLoadingIndicator = styled(InlineLoadingIndicator)<WithVisibleProps>`
  position: absolute;
  transition: opacity 0.2s;
  ${(p) =>
    !p.isVisible &&
    css`
      opacity: 0;
    `};
`;

const ButtonChildren = styled('div').attrs(({ isVisible }: WithVisibleProps) => ({
  ['aria-hidden']: !isVisible,
}))<WithVisibleProps>`
  width: 100%;
  display: flex;
  flex-wrap: unset;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  ${(p) =>
    !p.isVisible &&
    css`
      opacity: 0;
    `};
`;

const LoadingProgressBackground = styled.div<{
  xValue: number;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  display: block;
  background: rgba(0, 0, 0, 0.2);
  z-index: 0;
  transform: translateX(${({ xValue }) => `${xValue}%`});
  transition: all 0.7s ease-out;
`;

const styledButtonCallback = ({
  styles,
  hasLoadingBackground,
}: {
  styles: ButtonComponentStyles | undefined;
  hasLoadingBackground: boolean;
}) => ({
  // Fixed
  boxSizing: 'border-box',
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  borderStyle: 'solid',
  whiteSpace: 'nowrap',
  transition: TransitionDuration.Fast,
  overflow: hasLoadingBackground ? 'hidden' : 'initial',

  // TODO: not yet supported, but should be
  // background-image
  // text transform

  // Customizable
  // Layout
  height: sizeCss(styles?.['height'].base.base),
  width: sizeCss(styles?.['width'].base.base),
  marginTop: sizeCss(styles?.margin.base.base?.top),
  marginBottom: sizeCss(styles?.margin.base.base?.bottom),
  marginLeft: sizeCss(styles?.margin.base.base?.left),
  marginRight: sizeCss(styles?.margin.base.base?.right),
  paddingTop: sizeCss(styles?.padding.base.base?.top),
  paddingBottom: sizeCss(styles?.padding.base.base?.bottom),
  paddingLeft: sizeCss(styles?.padding.base.base?.left),
  paddingRight: sizeCss(styles?.padding.base.base?.right),
  justifySelf: styles?.['justify'].base.base,
  // Text
  ...(styles?.['font-family']?.base.base != null && {
    fontFamily: fontFamilyCss(styles?.['font-family'].base.base),
  }),
  fontSize: sizeCss(styles?.['font-size'].base.base),
  fontWeight: fontWeightCss(styles?.['font-weight'].base.base),
  lineHeight: sizeCss(styles?.['line-height'].base.base),
  color: colorCss(styles?.['text-color'].base.base),
  'svg path': {
    stroke: colorCss(styles?.['text-color'].base.base),
  },
  textTransform: styles?.['text-transform']?.base?.base || StyleValueTextTransform.None,
  // Decoration
  backgroundColor: colorCss(styles?.['background-color'].base.base),
  borderColor: colorCss(styles?.['border-color'].base.base),
  borderTopWidth: sizeCss(styles?.['border-width'].base.base?.top),
  borderBottomWidth: sizeCss(styles?.['border-width'].base.base?.bottom),
  borderLeftWidth: sizeCss(styles?.['border-width'].base.base?.left),
  borderRightWidth: sizeCss(styles?.['border-width'].base.base?.right),
  borderRadius: sizeCss(styles?.['border-radius'].base.base),
  boxShadow: `${sizeCss(styles?.['shadow-offset'].base.base?.x)} ${sizeCss(
    styles?.['shadow-offset'].base.base?.y,
  )} ${sizeCss(styles?.['shadow-radius'].base.base)} 0 ${colorCss(
    styles?.['shadow-color'].base.base,
  )}`,
  transform: `translateX(${sizeCss(styles?.['translate'].base.base?.x)}) translateY(${sizeCss(
    styles?.['translate'].base.base?.y,
  )})`,

  // Use :not(:disabled) instead of :enabled to support anchors. Anchors don't support :enabled.
  '&:hover:not(:disabled), &:focus:not(:disabled)': {
    color: colorCss(styles?.['text-color'].base.focused),
    backgroundColor: colorCss(styles?.['background-color'].base.focused),
    borderColor: colorCss(styles?.['border-color'].base.focused),
    boxShadow: `${sizeCss(styles?.['shadow-offset'].base.focused?.x)} ${sizeCss(
      styles?.['shadow-offset'].base.focused?.y,
    )} ${sizeCss(styles?.['shadow-radius'].base.focused)} 0 ${colorCss(
      styles?.['shadow-color'].base.focused,
    )}`,
  },

  '&:active:not(:disabled)': {
    color: colorCss(styles?.['text-color'].base.active),
    backgroundColor: colorCss(styles?.['background-color'].base.active),
    borderColor: colorCss(styles?.['border-color'].base.active),
    boxShadow: `${sizeCss(styles?.['shadow-offset'].base.active?.x)} ${sizeCss(
      styles?.['shadow-offset'].base.active?.y,
    )} ${sizeCss(styles?.['shadow-radius'].base.active)} 0 ${colorCss(
      styles?.['shadow-color'].base.active,
    )}`,
    transform: `translateX(${sizeCss(styles?.['translate'].base.active?.x)}) translateY(${sizeCss(
      styles?.['translate'].base.active?.y,
    )})`,
  },

  '&:disabled': {
    cursor: 'not-allowed',
    color: colorCss(styles?.['text-color'].base.disabled),
    backgroundColor: colorCss(styles?.['background-color'].base.disabled),
    borderColor: colorCss(styles?.['border-color'].base.disabled),
    boxShadow: `${sizeCss(styles?.['shadow-offset'].base.disabled?.x)} ${sizeCss(
      styles?.['shadow-offset'].base.disabled?.y,
    )} ${sizeCss(styles?.['shadow-radius'].base.disabled)} 0 ${colorCss(
      styles?.['shadow-color'].base.disabled,
    )}`,
    transform: `translateX(${sizeCss(styles?.['translate'].base.disabled?.x)}) translateY(${sizeCss(
      styles?.['translate'].base.disabled?.y,
    )})`,
  },

  '&:not(:active):not(:disabled)': {
    '&:hover, &:focus': {
      transform: `translateX(${sizeCss(
        styles?.['translate'].base.focused?.x,
      )}) translateY(${sizeCss(styles?.['translate'].base.focused?.y)})`,
    },
  },

  // Always show a transparent outline so animation isn't janky and only fades in
  outlineColor: '#00000000',
  outlineWidth: '2px',
  outlineOffset: '2px',
  outlineStyle: 'solid',

  '&:focus-visible': {
    outlineColor: colorCss(styles?.['border-color'].base.focused),
    outlineWidth: '2px',
    outlineOffset: '2px',
    outlineStyle: 'solid',
  },
});

const StyledLink = styled.a<{
  styles: ButtonComponentStyles | undefined;
  hasLoadingBackground: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
}>(styledButtonCallback);

const StyledButton = styled.button<{
  styles: ButtonComponentStyles | undefined;
  hasLoadingBackground: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
}>(styledButtonCallback);

const Button = (props: ButtonProps) => {
  const {
    children,
    // When type is omitted and inside a <form/>, <button/>s submit the form.
    // This is really confusing and has caused plenty of bugs, so we require
    // this behavior to be explicitly requested by passing type = 'submit.'
    href,
    type = 'button',
    styles,
    isPending,
    uploadProgress,
    pendingContent,
    ...otherProps
  } = props;

  const loadingBackgroundXValue = uploadProgress != null ? (100 - uploadProgress) * -1 : -100;

  const buttonContents = (
    <>
      {isPending && uploadProgress != null && (
        <LoadingProgressBackground
          xValue={loadingBackgroundXValue}
          role='progressbar'
          aria-busy
          aria-live='polite'
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={uploadProgress}
        />
      )}
      {pendingContent ? (
        <>
          {isPending ? (
            <ButtonChildren isVisible={true}>
              <span>{pendingContent}</span>
              <InlineLoadingIndicator />
            </ButtonChildren>
          ) : (
            <ButtonChildren isVisible={true} data-test='button__children'>
              {children}
            </ButtonChildren>
          )}
        </>
      ) : (
        <>
          {isPending && <StyledLoadingIndicator isVisible={isPending} />}
          <ButtonChildren isVisible={!isPending} data-test='button__children'>
            {children}
          </ButtonChildren>
        </>
      )}
    </>
  );

  if (href) {
    return (
      <StyledLink
        href={href}
        data-test={'button__' + props.buttonType}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        styles={styles}
        hasLoadingBackground={isPending && uploadProgress != null}
        target='_blank'
        rel='noopener'
        {...otherProps}
      >
        {buttonContents}
      </StyledLink>
    );
  } else {
    return (
      <StyledButton
        data-test={'button__' + props.buttonType}
        type={type}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        styles={styles}
        hasLoadingBackground={isPending && uploadProgress != null}
        {...otherProps}
      >
        {buttonContents}
      </StyledButton>
    );
  }
};

export default Button;
